import React, {
  useRef,
  useEffect,
  useState,
  useCallback,
  useMemo,
} from 'react';
import ReactDOM from 'react-dom';

import PointerWhite from 'assets/images/marker/white.svg';
import { Button } from 'components/Atoms';
import { useStore, useHeader, useModalMobileContext } from 'hooks';
import { LatLngTuple } from 'leaflet';
import LoadingPop from 'pages/Loading';

import { NoFavorites } from '../NoFavorites';
import { PlaceItem } from '../PlacesItem';
import { IPlace } from '../PlacesItem/interfaces';
import { RequestLocation } from '../RequestLocation';
import { TLatLng } from '../RequestLocation/interfaces';
import { SuggestionItem } from '../SuggestionItem';
import * as T from './interfaces';
import { SearchContainer } from './SearchContainer';
import * as S from './styles';

export const Container = ({
  isMobileDevice,
  changePosition,
  mustRender,
  changeChildRender,
}: T.ISearchContainer) => {
  const {
    listPlacesSuggestion,
    session,
    mapMode,
    openModal,
    places,
    placeSelected,
    userPosition,
    favorites,
    changeUserPosition,
    handleModal,
    loadListPlaces,
    handleCreateOverlay,
    handleRemoveOverlay,
    handleFirstTimeAskingForSharing,
  } = useStore();
  const { showHeader } = useHeader();

  const scrollRef = useRef<HTMLOListElement>(null);
  const [positionScroll, setPositionScroll] = useState<number>(0);
  const [permissionLocationUser, setPermissionLocationUser] = useState(false);
  const { handleSetModalMode } = useModalMobileContext();

  const handleGetPosition = useCallback(
    (position: TLatLng) => {
      const positionUser: {
        isSharing: boolean;
        position?: LatLngTuple;
      } = position
        ? {
            isSharing: true,
            position: [position.lat, position.lng],
          }
        : {
            isSharing: false,
          };
      changeUserPosition(positionUser);
    },
    [changeUserPosition],
  );

  useEffect(() => {
    handleSetModalMode(mapMode);
  }, [mapMode]);

  useEffect(() => {
    async function verifyUserPermission() {
      const isFirstTime =
        userPosition?.firstTimeAskingForSharing ||
        userPosition?.firstTimeAskingForSharing === null;
      if (isFirstTime) {
        changeChildRender('request');
        handleCreateOverlay();
      }
      await navigator.geolocation.getCurrentPosition(
        pos => {
          if (isFirstTime) {
            handleRemoveOverlay();
            handleFirstTimeAskingForSharing();
          }

          changeChildRender('list');
          setPermissionLocationUser(true);
          handleGetPosition({
            lat: pos.coords.latitude,
            lng: pos.coords.longitude,
          });
        },
        error => {
          if (isFirstTime) {
            handleRemoveOverlay();
            handleFirstTimeAskingForSharing();
          }
          changeChildRender('list');
          handleGetPosition(null);
        },
      );
    }
    try {
      verifyUserPermission();
    } catch (error) {
      console.log(error);
    }

    loadListPlaces();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleScroll = useCallback(
    (e: React.UIEvent<HTMLOListElement, UIEvent>) => {
      const { scrollTop } = e.currentTarget;
      setPositionScroll(scrollTop);
    },
    [setPositionScroll],
  );

  const PlacesList = useMemo(
    () =>
      places.map((place, index) => (
        <PlaceItem key={`${place.id}`} id={`${place.id}`} place={place} />
      )),
    [places],
  );

  const FavoritesList = useMemo(
    () =>
      places
        .filter(place => favorites.includes(place?.bk_number))
        .map((place, index) => (
          <PlaceItem
            key={`${place.id}${index}`}
            id={`${place.id}${index}`}
            place={place}
          />
        )),
    [places, favorites],
  );

  const ListSuggestion = useMemo(
    () =>
      listPlacesSuggestion.map((suggestion, index) => (
        <SuggestionItem
          key={`palceItem${index}`}
          id={`placesAutocomplete@$item${index}`}
          data-testid={`placesAutocomplete@item${index}`}
        >
          {suggestion}
        </SuggestionItem>
      )),
    [listPlacesSuggestion],
  );

  const GetLocation = useMemo(
    () => (
      <S.WrapperUniqChild>
        <RequestLocation
          toggleLatLng={(position: TLatLng) => {
            handleGetPosition(position);
            changeChildRender('list');
          }}
        />
      </S.WrapperUniqChild>
    ),
    [],
  );

  const ComponentChildren = useMemo(() => {
    if (session === 'nearStores') {
      if (mustRender === 'request') {
        return GetLocation;
      }
      if (mustRender === 'list') {
        if (listPlacesSuggestion.length > 0) {
          return ListSuggestion;
        }
        if (places.length > 0) {
          return PlacesList;
        }
      } else {
        return LoadingPop;
      }
    } else if (session === 'favoriteStores') {
      return (
        <S.WrapperUniqChild>
          {favorites.length > 0 ? FavoritesList : <NoFavorites />}
        </S.WrapperUniqChild>
      );
    }
  }, [mustRender, session, listPlacesSuggestion, places]);

  const handleRenderBannerPosition = useCallback(async () => {
    if (isMobileDevice && mapMode && !permissionLocationUser) {
      setTimeout(() => {
        const Overlay = document.getElementById(`modalMobile@PlaceItem`);
        if (!Overlay) return;
        const Modal = Overlay.children[0];
        const child = document.createElement('div');
        child.id = 'modalMobile@PlaceItem@banner';
        child.className = 'banner_modal';
        Modal.appendChild(child);

        ReactDOM.render(
          <Button
            id="modalMobile@PlaceItem@banner@getPosition"
            variant="ghost"
            title="Acione para liberar sua localização"
            noHeritage
            onClick={changePosition}
            type="button"
          >
            <S.ChildBannerPosition>
              <img alt="icone de ponteiro de localização" src={PointerWhite} />
              <span>Usar minha localização</span>
            </S.ChildBannerPosition>
          </Button>,
          child,
        );
      }, 0);
    }
  }, [isMobileDevice, mapMode, permissionLocationUser]);

  useEffect(() => {
    if (userPosition?.isSharing) {
      const element: any = document.getElementById(
        'modalMobile@PlaceItem@banner',
      );
      element && element.remove();
    }
  }, [userPosition]);

  useEffect(() => {
    handleRenderBannerPosition();
  }, [mapMode, isMobileDevice, openModal, permissionLocationUser]);

  useEffect(() => {
    if (mustRender === 'request') {
      const List = document.getElementById('searchList');
      List.scrollTop = List.scrollHeight;
    }
  }, [mustRender]);

  return (
    <SearchContainer
      noScroll={mustRender === 'request'}
      isMobileDevice={isMobileDevice}
      ref={scrollRef}
      onScroll={handleScroll}
      heightHeader={positionScroll}
      showHeader={showHeader}
      openModal={openModal}
      handleModal={handleModal}
      placeSelected={placeSelected}
      mapMode={mapMode}
      haveSuggestionPlaces={listPlacesSuggestion.length > 0}
      style={
        listPlacesSuggestion?.length >= 1 && mapMode
          ? {
              zIndex: 25,
              background: 'white',
              minHeight: '100%',
              height: '100%',
              backgroundColor: 'white',
              transition: 'all 0s',
            }
          : { transition: 'all 0s' }
      }
    >
      {ComponentChildren}
    </SearchContainer>
  );
};
